/**
 * 個人スマホのホームに出す「ポータルの今」カード（機能フラグ portal の会社のみ）。
 * 未読の連絡・要確認マニュアル・チャット未読をひと目で見せ、最新のお知らせ3件へ直接飛べる。
 * 圏外でもSWキャッシュの前回値で表示される（取得失敗時はカードごと非表示）。
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/api';
import { PIcon } from './PIcon';

interface Summary {
  board_unread: number; chat_unread: number; manuals_unread: number;
  my_expenses_active: number; my_requests_pending: number;
  recent_posts: Array<{ id: string; post_type: string; title: string; created_at: string; my_read: boolean }>;
}

export const PortalSummaryCard: React.FC = () => {
  const nav = useNavigate();
  const [sum, setSum] = useState<Summary | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiFetch<{ data: Summary }>('/portal/summary', {}, { noRedirect: true })
        .then(r => { if (!cancelled) setSum(r.data); })
        .catch(() => { /* フラグOFF・初回圏外時は非表示のまま */ });
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!sum) return null;

  const chips: Array<{ label: string; count: number; path: string; emoji: string }> = [
    { emoji: 'megaphone', label: '連絡', count: sum.board_unread, path: '/portal-board' },
    { emoji: 'chat', label: 'チャット', count: sum.chat_unread, path: '/portal-chat' },
    { emoji: 'book', label: '要確認', count: sum.manuals_unread, path: '/portal-manuals' },
    { emoji: 'filecheck', label: '申請中', count: sum.my_requests_pending + sum.my_expenses_active, path: '/portal-requests' },
  ];

  return (
    <div style={{ background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 16, padding: '14px 14px 10px', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <PIcon name="home" size={15} color="#0c5f59" />
        <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>ポータル</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>会社の連絡・申請・マニュアル</span>
      </div>

      {/* 状況チップ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 8 }}>
        {chips.map(c => (
          <button key={c.path + c.label} onClick={() => nav(c.path)}
            style={{
              // ピンクの面で埋めると警告だらけに見えるため、地は常に白・件数の色だけで知らせる
              border: '1.5px solid #e3e6ea', background: '#fff',
              borderRadius: 12, padding: '9px 2px', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            }}>
            <PIcon name={c.emoji} size={16} color="#0c5f59" />
            <span style={{ fontSize: 10.5, color: '#64748b', fontWeight: 700 }}>{c.label}</span>
            <span style={{ fontSize: 13, fontWeight: 900, color: c.count > 0 ? '#dc2626' : '#64748b' }}>{c.count}</span>
          </button>
        ))}
      </div>

      {/* 最新のお知らせ */}
      {sum.recent_posts.slice(0, 3).map(p => (
        <button key={p.id} onClick={() => nav('/portal-board')}
          style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left', padding: '7px 2px', border: 'none', borderTop: '1px solid #f1f5f9', background: 'none', cursor: 'pointer' }}>
          <span style={{
            fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 20, flexShrink: 0,
            background: p.post_type === 'circular' ? '#fef3c7' : '#e6f4f1',
            color: p.post_type === 'circular' ? '#b45309' : '#0f766e',
          }}>{p.post_type === 'circular' ? '回覧' : 'お知らせ'}</span>
          <span style={{ fontSize: 12.5, color: p.my_read ? '#64748b' : '#0f172a', fontWeight: p.my_read ? 500 : 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {p.title}
          </span>
          {!p.my_read && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#dc2626', flexShrink: 0 }} />}
        </button>
      ))}
    </div>
  );
};
